'use client';

import { X, BarChart2, Activity, Trophy } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';
import { useLang } from '@/lib/i18n/LanguageContext';


interface HowItWorksModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const STEPS = [
  {
    icon: BarChart2,
    color: 'var(--info)',
    bg: 'var(--info-muted)',
    title: 'Post your chart',
    desc: 'Share your trade idea with Entry, Stop Loss and Take Profit levels. Upload a chart screenshot or let us auto-generate a chart from your levels.',
  },
  {
    icon: Activity,
    color: 'var(--bullish)',
    bg: 'var(--bullish-muted)',
    title: 'System tracks your trade',
    desc: 'ShowMeTrade automatically monitors your trade in real-time. When price hits your TP or SL, your result is recorded publicly — no editing allowed.',
  },
  {
    icon: Trophy,
    color: 'var(--win)',
    bg: 'var(--win-muted)',
    title: 'Get ranked by real performance',
    desc: 'Your win rate, R:R ratio, and consistency score are calculated from actual closed trades — not followers or likes. Earn badges and climb the leaderboard.',
  },
];

export default function HowItWorksModal({ isOpen, onClose }: HowItWorksModalProps) {
  const { t } = useLang();
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center p-4 animate-fade-in"
      style={{ background: 'rgba(28,43,38,0.6)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-md rounded-2xl overflow-hidden animate-slide-up"
        style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', boxShadow: '0 24px 64px rgba(28,43,38,0.25)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b"
          style={{ borderColor: 'var(--border)' }}
        >
          <div>
            <h2 className="font-bold text-base font-heading" style={{ color: 'var(--text-primary)' }}>{t('How ShowMeTrade Works')}</h2>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{t('Transparent trading performance, publicly tracked')}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg transition-colors duration-100"
            style={{ color: 'var(--text-muted)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-sunken)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-primary)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)'; }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Steps */}
        <div className="p-5 flex flex-col gap-4">
          {STEPS.map((step, idx) => {
            const Icon = step.icon;
            return (
              <div key={idx} className="flex gap-4">
                {/* Step number + connector */}
                <div className="flex flex-col items-center">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: step.bg, border: `1px solid ${step.color}` }}
                  >
                    <Icon size={17} style={{ color: step.color }} />
                  </div>
                  {idx < STEPS.length - 1 && (
                    <div className="w-px flex-1 mt-2" style={{ background: 'var(--border)', minHeight: 20 }} />
                  )}
                </div>

                {/* Content */}
                <div className="pb-4 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="text-xs font-bold px-1.5 py-0.5 rounded"
                      style={{ background: step.bg, color: step.color }}
                    >
                      {t('Step')} {idx + 1}
                    </span>
                  </div>
                  <h3 className="text-sm font-bold mb-1" style={{ color: 'var(--text-primary)' }}>{t(step.title)}</h3>
                  <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{t(step.desc)}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer CTA */}
        <div className="px-5 pb-5">
          <div
            className="p-3 rounded-xl text-center"
            style={{ background: 'var(--accent-muted)', border: '1px solid rgba(12,91,65,0.2)' }}
          >
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
              🏆 <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{t('Credibility is earned, not bought.')}</span>{' '}
              {t('Every badge and ranking is based on verified closed trades.')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="btn-primary w-full mt-3 py-2.5 text-sm"
          >
            {t("Got it — let's trade")}
          </button>
        </div>
      </div>
    </div>
  );
}
